import { useState } from "react";
import { Clock, FileText, History, Loader2, Plus, RotateCcw, X } from "lucide-react";
import {
  useCreateVersion,
  useProposalVersion,
  useProposalVersions,
  useRestoreVersion,
} from "@/hooks/use-proposals";
import { sanitizeHtml } from "@/lib/sanitize-html";

interface VersionHistoryProps {
  proposalId: string;
  isOpen: boolean;
  onClose: () => void;
}

function formatDate(dateString: string): string {
  const date = new Date(dateString);
  return new Intl.DateTimeFormat("es-ES", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
}

/**
 * Drawer modal con el historial de versiones de una propuesta.
 *
 * Lista los snapshots, permite ver el detalle de cualquier versión y restaurar
 * un snapshot. Toda la lógica de red pasa por hooks de React Query
 * (`useProposalVersions`, `useProposalVersion`, `useCreateVersion`,
 * `useRestoreVersion`) que se encargan de invalidar caché y mostrar toasts.
 */
export function VersionHistory({ proposalId, isOpen, onClose }: VersionHistoryProps) {
  const [selectedVersionId, setSelectedVersionId] = useState<string | null>(null);

  const versionsQuery = useProposalVersions(isOpen ? proposalId : null);
  const versionDetailQuery = useProposalVersion(
    isOpen ? proposalId : null,
    selectedVersionId
  );
  const createVersionMutation = useCreateVersion(proposalId);
  const restoreMutation = useRestoreVersion(proposalId);

  const versions = versionsQuery.data ?? [];
  const selectedVersion = versionDetailQuery.data;

  const handleClose = () => {
    setSelectedVersionId(null);
    onClose();
  };

  const handleCreateSnapshot = async () => {
    try {
      await createVersionMutation.mutateAsync({
        change_summary: `Snapshot manual - ${new Date().toLocaleString("es-ES")}`,
      });
    } catch {
      // toast manejado por el hook
    }
  };

  const handleRestore = async (versionId: string) => {
    if (
      !window.confirm(
        "¿Restaurar esta versión? El contenido actual del editor será reemplazado por el snapshot."
      )
    ) {
      return;
    }
    try {
      await restoreMutation.mutateAsync({ versionId });
      handleClose();
    } catch {
      // toast manejado por el hook
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      onClick={handleClose}
      role="dialog"
      aria-modal="true"
      aria-label="Historial de versiones"
    >
      <div
        className="bg-white rounded-lg shadow-xl max-w-5xl w-full max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <History className="w-5 h-5 text-gray-700" />
            <h2 className="text-xl font-semibold text-gray-800">Historial de versiones</h2>
          </div>
          <button
            type="button"
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            aria-label="Cerrar"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="flex-1 overflow-hidden flex">
          {/* Lista de versiones */}
          <aside className="w-1/3 border-r border-gray-200 overflow-y-auto flex flex-col">
            <div className="p-4 border-b border-gray-200">
              <button
                type="button"
                onClick={handleCreateSnapshot}
                disabled={createVersionMutation.isPending}
                className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {createVersionMutation.isPending ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Creando snapshot...
                  </>
                ) : (
                  <>
                    <Plus className="w-4 h-4" />
                    Crear snapshot ahora
                  </>
                )}
              </button>
            </div>

            <div className="flex-1 overflow-y-auto">
              {versionsQuery.isLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-6 h-6 animate-spin text-blue-500" />
                </div>
              ) : versions.length === 0 ? (
                <div className="p-8 text-center text-gray-500">
                  <Clock className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  <p className="font-medium">No hay versiones guardadas</p>
                  <p className="text-sm mt-1">
                    Crea un snapshot para guardar el estado actual
                  </p>
                </div>
              ) : (
                <div className="divide-y divide-gray-200">
                  {versions.map((version) => (
                    <button
                      type="button"
                      key={version.id}
                      onClick={() => setSelectedVersionId(version.id)}
                      className={`w-full p-4 text-left hover:bg-gray-50 transition-colors ${
                        selectedVersionId === version.id ? "bg-blue-50" : ""
                      }`}
                    >
                      <div className="flex items-start justify-between mb-2">
                        <span className="font-semibold text-gray-800">
                          Versión {version.version_number}
                        </span>
                        <span className="text-xs text-gray-500">
                          {formatDate(version.created_at)}
                        </span>
                      </div>
                      {version.change_summary && (
                        <p className="text-sm text-gray-600 mb-2 line-clamp-2">
                          {version.change_summary}
                        </p>
                      )}
                      <p className="text-xs text-gray-500">Por: {version.created_by}</p>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </aside>

          {/* Detalle de la versión seleccionada */}
          <section className="flex-1 overflow-y-auto">
            {!selectedVersionId ? (
              <div className="flex items-center justify-center h-full text-gray-400">
                <div className="text-center">
                  <FileText className="w-16 h-16 mx-auto mb-4" />
                  <p>Selecciona una versión para ver los detalles</p>
                </div>
              </div>
            ) : versionDetailQuery.isLoading ? (
              <div className="flex items-center justify-center h-full">
                <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
              </div>
            ) : versionDetailQuery.isError || !selectedVersion ? (
              <div className="flex items-center justify-center h-full text-red-500">
                <p>Error al cargar la versión</p>
              </div>
            ) : (
              <div className="p-6">
                <div className="mb-6">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-800">
                        Versión {selectedVersion.version_number}
                      </h3>
                      <p className="text-sm text-gray-500">
                        {formatDate(selectedVersion.created_at)} por {selectedVersion.created_by}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => handleRestore(selectedVersion.id)}
                      disabled={restoreMutation.isPending}
                      className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {restoreMutation.isPending ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          Restaurando...
                        </>
                      ) : (
                        <>
                          <RotateCcw className="w-4 h-4" />
                          Restaurar esta versión
                        </>
                      )}
                    </button>
                  </div>

                  {selectedVersion.change_summary && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
                      <p className="text-sm text-blue-800">
                        <strong>Resumen de cambios:</strong> {selectedVersion.change_summary}
                      </p>
                    </div>
                  )}
                </div>

                <div className="border-t border-gray-200 pt-6">
                  <h4 className="font-semibold text-gray-800 mb-4">
                    Contenido ({selectedVersion.snapshot_data.sections?.length || 0} secciones)
                  </h4>
                  <div className="space-y-4">
                    {[...(selectedVersion.snapshot_data.sections ?? [])]
                      .sort((a, b) => a.section_order - b.section_order)
                      .map((section) => (
                        <div
                          key={section.id}
                          className="border border-gray-200 rounded-lg p-4"
                        >
                          <h5 className="font-medium text-gray-800 mb-2">{section.title}</h5>
                          <div
                            className="prose prose-sm max-w-none text-gray-600"
                            dangerouslySetInnerHTML={{
                              __html: section.content_html
                                ? sanitizeHtml(section.content_html)
                                : "<p class='text-gray-400 italic'>Sin contenido</p>",
                            }}
                          />
                        </div>
                      ))}
                  </div>
                </div>
              </div>
            )}
          </section>
        </div>
      </div>
    </div>
  );
}

export default VersionHistory;
